import { useEffect, useState } from 'react';
import _difference from 'lodash/difference';
import _get from 'lodash/get';
import _map from 'lodash/map';
import _omit from 'lodash/omit';
import _sortBy from 'lodash/sortBy';
import _union from 'lodash/union';

const flowKeysToOmit = [
    'flowSettings',
    'isComplete',
    'isStarted',
    'progressBarGroup',
    'showIfOption',
    'surveySettings',
];

export default ({
    allowRetake,
    language,
    flowList,
    mustTakeInOrder,
    mediatorProps,
    onAlreadyComplete,
    onAssignmentComplete,
    onModuleComplete,
    onComplete,
    onFlowComplete,
    onFlowStarted,
}) => {
    const [completedFlows, setCompletedFlows] = useState(
        flowList.filter(item => item.isComplete).map(item => item.handle)
    );

    const [hasCompleted, setHasCompleted] = useState(
        !flowList.filter(f => f.isRequired).find(f => !f.isComplete)
    );
    const [startedFlows, setStartedFlows] = useState(
        flowList.filter(item => item.isStarted).map(item => item.handle)
    );

    const [nextFlowHandle, setNextFlowHandle] = useState(null);
    const [orderedFlowHandles, setOrderedFlowHandles] = useState([]);

    const [currentFlowHandle, setCurrentFlowHandle] = useState(null);
    const currentFlowFromFlowList = flowList.find(item => item.handle === currentFlowHandle);

    const [isLoading, setIsLoading] = useState(false);
    const [modules, setModules] = useState(
        mediatorProps.modules.filter(m => m.language === language)
    );

    const determineStartingModule = () => {
        const incompleteFlows = flowList.filter(f => !f.isComplete);
        const firstModule = _map(_sortBy(incompleteFlows, 'moduleId'), 'moduleId');
        if (firstModule.length) {
            return firstModule[0];
        }
        return 1;
    };

    const navItems = flowList.map(flow => {
        let isAllowed = !mustTakeInOrder;
        const isCompleted = completedFlows.includes(flow.handle);
        const isStarted = startedFlows.includes(flow.handle);
        const isResume = !isCompleted && isStarted;

        if (
            isCompleted ||
            isStarted ||
            flow.handle === nextFlowHandle ||
            flowList
                .filter(f => f.ordinalPosition < flow.ordinalPosition)
                .every(f => !f.isRequired || completedFlows.includes(f.handle))
        ) {
            isAllowed = true;
        }

        return {
            ..._omit(flow, flowKeysToOmit),
            title: _get(flow, 'flowSettings.playerTemplateSettings.title', flow.handle),
            isCompleted,
            isAllowed,
            isResume,
            setCurrent: !isAllowed
                ? null
                : () => {
                      setCurrentFlowHandle(flow.handle);
                      setStartedFlows(_union(startedFlows, [flow.handle]).sort());
                  },
        };
    });

    useEffect(() => {
        if (!allowRetake && flowList.every(item => item.isComplete) && onAlreadyComplete) {
            onAlreadyComplete();
        }

        const startingModule = determineStartingModule();
        function packageModules() {
            const packagedModules = [];
            modules.sort((a, b) => a.id - b.id);
            modules.forEach((module, i) => {
                module.setActive = setActiveModuleFunc(module);
                module.isActive = Number.parseInt(module.id, 10) === startingModule;
                module.lessons = getLessonsByModuleId(module.id);
                // isComplete used to determine if module is passed; isAllLessonsComplete used for checkmark
                module.isComplete = module.lessons.every(
                    item => !item.isRequired || completedFlows.includes(item.handle)
                );
                module.isAllLessonsComplete = module.lessons.every(item =>
                    completedFlows.includes(item.handle)
                );
                module.isResume =
                    !module.isComplete &&
                    !!module.lessons.find(item => startedFlows.includes(item.handle));
                if (i === 0 || !mustTakeInOrder) {
                    module.isAllowed = true;
                } else {
                    module.isAllowed = packagedModules
                        .filter(m => m.id < module.id)
                        .every(m => m.isComplete);
                }
                module.isEmpty = module.lessons.length === 0;
                module.showDimmed =
                    (!module.isActive &&
                        !module.isResume &&
                        !module.isComplete &&
                        !module.isAllowed) ||
                    module.isEmpty;
                packagedModules.push(module);
            });
            const sortedPackagedModules = _sortBy(packagedModules, 'id');
            setModules(sortedPackagedModules);

            let ofh = [];
            sortedPackagedModules.forEach(module => {
                ofh = ofh.concat(_map(module.lessons, 'handle'));
            });
            setOrderedFlowHandles(ofh);
            checkNextFlowHandle(ofh);
        }

        packageModules();
    }, []);

    useEffect(() => {
        function updateModules() {
            const packagedModules = [];
            modules.sort((a, b) => a.id - b.id);
            modules.forEach((module, i) => {
                module.lessons = getLessonsByModuleId(module.id);
                module.isComplete = module.lessons.every(
                    item => !item.isRequired || completedFlows.includes(item.handle)
                );
                module.isAllLessonsComplete = module.lessons.every(item =>
                    completedFlows.includes(item.handle)
                );
                module.isResume =
                    !module.isComplete &&
                    !!module.lessons.find(item => startedFlows.includes(item.handle));
                if (i === 0 || !mustTakeInOrder) {
                    module.isAllowed = true;
                } else {
                    module.isAllowed = packagedModules
                        .filter(m => m.id < module.id)
                        .every(m => m.isComplete);
                }
                module.showDimmed =
                    !module.isAllowed ||
                    (!module.isActive &&
                        !module.isResume &&
                        !module.isComplete &&
                        !module.isAllowed) ||
                    module.lessons.length === 0;
                packagedModules.push(module);
            });
            setModules(packagedModules);
        }

        checkNextFlowHandle(orderedFlowHandles);

        updateModules();
    }, [startedFlows, completedFlows, nextFlowHandle]);

    useEffect(() => {
        if (!currentFlowFromFlowList) {
            return;
        }
        onFlowStarted(currentFlowFromFlowList);
    }, [currentFlowHandle]);

    /**
     * If applicable, set the next flow handle.
     *
     * @param Array ofh The orderedFlowHandles array to consider
     */
    function checkNextFlowHandle(ofh) {
        if (startedFlows.length !== completedFlows.length || startedFlows.length === ofh.length) {
            return;
        }
        if (startedFlows.every(sf => completedFlows.some(cf => cf.handle === sf.handle))) {
            const determinedNextHandle = _difference(ofh, startedFlows)[0];
            if (determinedNextHandle !== nextFlowHandle) {
                setNextFlowHandle(determinedNextHandle);
            }
        }
    }

    function setActiveModuleFunc(module) {
        return () => {
            setModules(
                modules.map(m => {
                    m.isActive = m.id === module.id;
                    return m;
                })
            );
        };
    }

    function getLessonsByModuleId(moduleId) {
        const lessons = [];
        navItems.forEach(f => {
            if (Number.parseInt(f.moduleId, 10) === Number.parseInt(moduleId, 10)) {
                lessons.push(f);
            }
        });
        return _sortBy(lessons, 'ordinalPosition');
    }

    function getCurrentFlowProps() {
        if (!currentFlowHandle) {
            return null;
        }
        const currentFlowIndex = navItems.findIndex(item => item.handle === currentFlowHandle);
        const currentFlow = navItems[currentFlowIndex];

        return {
            ..._omit(currentFlow, flowKeysToOmit),
            ...(currentFlow.isCompleted && { pageFirstElementId: 0 }),
            onComplete: () => {
                completeCurrentFlow();
            },
        };
    }

    async function completeCurrentFlow() {
        setIsLoading(true);
        const currModule = modules.find(item => item.isActive);
        const isFirstCompletion = currentFlowFromFlowList.isComplete === false;

        await onFlowComplete(currentFlowFromFlowList, {
            isFirstCompletion,
            moduleId: currModule.id,
        });

        const updatedCompletedFlows = _union(completedFlows, [currentFlowHandle]);

        const requiredFlows = flowList.filter(item => item.isRequired);
        if (
            !hasCompleted &&
            requiredFlows.every(item => updatedCompletedFlows.includes(item.handle))
        ) {
            if (onAssignmentComplete) {
                await onAssignmentComplete();
            }
            setHasCompleted(true);
        }

        if (updatedCompletedFlows.length === flowList.length && onComplete) {
            await onComplete();
        }

        if (
            !currModule.isComplete &&
            currModule.lessons.every(
                item => !item.isRequired || updatedCompletedFlows.includes(item.handle)
            )
        ) {
            if (onModuleComplete) {
                await onModuleComplete({ moduleId: currModule.id, isFirstCompletion });
            }
            currModule.isComplete = true;
        }

        setCompletedFlows(updatedCompletedFlows.sort());
        setCurrentFlowHandle(null);
        setIsLoading(false);
    }

    return {
        closeFlowPlayer: () => setCurrentFlowHandle(null),
        currentFlowProps: getCurrentFlowProps(),
        modules,
        isLoading,
    };
};
